import React, { useState, useEffect } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import axios from 'axios';


function Chat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [threadId, setThreadId] = useState(null);
  const [registered, setRegistered] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [formErrors, setFormErrors] = useState({});
  const [defaultCountry, setDefaultCountry] = useState('');

  // Check registration on mount
  useEffect(() => {
    const reg = localStorage.getItem('chatbot_registration');
    if (reg) {
      const { date } = JSON.parse(reg);
      const today = new Date().toISOString().slice(0, 10);
      if (date === today) setRegistered(true);
    }
    // Fetch country code from ipwho
    fetch('https://ipwho.is/')
      .then(res => res.json())
      .then(data => {
        if (data && data.country_code) {
          setDefaultCountry(data.country_code.toLowerCase());
        }
      });
  }, []);

  // Markdown-like parser for bot messages
  function parseBotMessage(text) {
    if (!text) return '';
    // Links: [text](url)
    let html = text.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank">$1</a>');
    // Bold: **text**
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    // Italic: *text* or _text_
    html = html.replace(/\*(?!\*)([^*]+)\*/g, '<em>$1</em>');
    html = html.replace(/_(.*?)_/g, '<em>$1</em>');
    // Ordered lists
    html = html.replace(/(?:^|\n)(\d+\. .+(?:\n\d+\. .+)*)/g, function(match) {
      const items = match.trim().split(/\n/).map(i => i.replace(/^\d+\. /, ''));
      return '<ol>' + items.map(i => '<li>' + i + '</li>').join('') + '</ol>';
    });
    // Unordered lists
    html = html.replace(/(?:^|\n)((?:[-*] .+(?:\n)?)+)/g, function(match) {
      const items = match.trim().split(/\n/).filter(Boolean).map(i => i.replace(/^[-*] /, ''));
      if (items.length > 1 || (items.length === 1 && match.trim().startsWith('- '))) {
        return '<ul>' + items.map(i => '<li>' + i + '</li>').join('') + '</ul>';
      }
      return match;
    });
    // Newlines
    html = html.replace(/\n/g, '<br />');
    return html;
  }

  // Handle registration form submit
  const handleRegister = (e) => {
    e.preventDefault();
    const errors = {};
    if (!form.name.trim()) {
      errors.name = 'Name is required.';
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      errors.email = 'Please enter a valid email address.';
    }
    // Phone: + followed by 10-15 digits (react-phone-input-2 always provides country code)
    if (!/^\+[0-9]{10,15}$/.test('+' + form.phone.replace(/\D/g, ''))) {
      errors.phone = 'Enter a valid phone number with country code.';
    }
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;
    const today = new Date().toISOString().slice(0, 10);
    localStorage.setItem('chatbot_registration', JSON.stringify({ ...form, date: today }));
    setRegistered(true);
  };

  const sendMessage = async () => {
    if (!input.trim()) return; // Ignore empty messages
    const userMessage = { sender: 'user', text: input };
    setMessages(prev => [...prev, userMessage]);

    // Get registration info from localStorage
    const reg = localStorage.getItem('chatbot_registration');
    let userInfo = { name: '', email: '', phone: '' };
    if (reg) {
      const { name, email, phone } = JSON.parse(reg);
      userInfo = { name, email, phone };
    }

    // Send threadId if available
    const payload = { message: input, ...userInfo };
    if (threadId) payload.threadId = threadId;
    setInput('');
    const res = await axios.post('http://localhost:5001/chat', payload);
    const botMessage = { sender: 'bot', text: res.data.reply };
    setMessages(prev => [...prev, botMessage]);

    // Save threadId from response if present
    if (res.data.threadId) setThreadId(res.data.threadId);

    setInput('');
  };

  if (!registered) {
    return (
      <div style={{ maxWidth: 400, margin: 'auto', marginTop: 40 }}>
        <h2>Welcome! Please Register</h2>
        <form onSubmit={handleRegister} style={{ display: 'flex', flexDirection: 'column', gap: 12 }} noValidate>
          <div>
            <input
              type="text"
              placeholder="Name"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              required
              style={{ width: '100%', ...(formErrors.name ? { borderColor: 'red' } : {}) }}
            />
            {formErrors.name && <div style={{ color: 'red', fontSize: 13 }}>{formErrors.name}</div>}
          </div>
          <div>
            <input
              type="email"
              placeholder="Email"
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              required
              style={{ width: '100%', ...(formErrors.email ? { borderColor: 'red' } : {}) }}
            />
            {formErrors.email && <div style={{ color: 'red', fontSize: 13 }}>{formErrors.email}</div>}
          </div>
          <div>
            <PhoneInput
              country={defaultCountry || 'IN'}
              value={form.phone}
              onChange={phone => setForm(f => ({ ...f, phone }))}
              inputStyle={formErrors.phone ? { borderColor: 'red', width: '100%' } : { width: '100%' }}
              inputProps={{ required: true, name: 'phone' }}
              enableSearch
            />
            {formErrors.phone && <div style={{ color: 'red', fontSize: 13 }}>{formErrors.phone}</div>}
          </div>
          <button type="submit">Register & Start Chat</button>
        </form>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 600, margin: 'auto' }}>
      <h2>LMS Chatbot</h2>
      <div style={{ height: 300, overflowY: 'auto', border: '1px solid #ccc', padding: 10 }}>
        {messages.map((m, i) => (
          <div key={i} style={{ textAlign: m.sender === 'user' ? 'right' : 'left' }}>
            {m.sender === 'bot' ? (
              <p>
                <strong>{m.sender}:</strong>{' '}
                <span
                  dangerouslySetInnerHTML={{
                    __html: parseBotMessage(m.text)
                  }}
                />
              </p>
            ) : (
              <p><strong>{m.sender}:</strong> {m.text}</p>
            )}
          </div>
        ))}
      </div>
      <input
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
        placeholder="Ask something..."
        style={{ width: '80%' }}
      />
      <button onClick={sendMessage}>Send</button>
    </div>
  );
}

export default Chat;
